import React, { useState, useEffect, useCallback } from "react";
import { Loader, Share2, Link as LinkIcon, Check } from "lucide-react";
import {
  initGA,
  trackPageView,
  trackComparison,
  trackCitySelection,
  trackShare,
  trackError,
  trackUserZipEntry,
  trackCitySearch,
} from "./analytics";

// Environment-based API URL with multiple fallback strategies
const getApiUrl = () => {
  // 1. Check for environment variable (set via .env files)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 2. Auto-detect based on hostname
  if (
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
  ) {
    return "https://rent-compare-backend-6z27tzkpdq-uc.a.run.app/api";
  }

  // 3. Default to local development
  return "http://localhost:8000/api";
};

const API_URL = getApiUrl();

console.log("🔗 Using API URL:", API_URL);

export default function RentApp() {
  const [userZip, setUserZip] = useState("");
  const [userRent, setUserRent] = useState("");
  const [userBedrooms, setUserBedrooms] = useState("1BR");
  const [selectedCities, setSelectedCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [availableCities, setAvailableCities] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [backendReady, setBackendReady] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Define handleSearch early so it can be used in useEffects
  const handleSearch = useCallback(async () => {
    if (!backendReady) {
      setError("Backend not ready");
      trackError("Backend Not Ready", "Backend not initialized");
      return;
    }

    if (!userZip || !userRent) {
      setError("Please enter zip code and rent amount");
      trackError("Validation Error", "Missing zip or rent");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Track user's ZIP code entry
      trackUserZipEntry(userZip, userBedrooms);

      // Track the comparison search
      trackComparison(userZip, selectedCities, userBedrooms);

      const response = await fetch(`${API_URL}/compare`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          zip_code: userZip,
          rent: parseFloat(userRent),
          bedrooms: userBedrooms,
          compare_cities: selectedCities.map((c) => `${c.city}, ${c.state}`),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.detail || data.error || "An error occurred";
        setError(errorMsg);
        setResults(null);
        trackError("API Error", errorMsg);
      } else {
        setResults(data);
      }
    } catch (err) {
      const errorMsg = "Error connecting to server: " + err.message;
      setError(errorMsg);
      setResults(null);
      trackError("Network Error", errorMsg);
    } finally {
      setLoading(false);
    }
  }, [backendReady, userZip, userRent, userBedrooms, selectedCities]);

  // Initialize Google Analytics on mount
  useEffect(() => {
    initGA();
    trackPageView(window.location.pathname + window.location.search);
  }, []);

  // Load data from URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const zip = params.get("zip");
    const rent = params.get("rent");
    const bedrooms = params.get("bedrooms");
    const cities = params.get("cities");
    const states = params.get("states");

    if (zip) setUserZip(zip);
    if (rent) setUserRent(rent);
    if (bedrooms) setUserBedrooms(bedrooms);

    // Load cities parameter after backend is ready
    if (cities && states && backendReady && availableCities.length > 0) {
      const cityNames = cities.split(",");
      const stateNames = states.split(",");

      // Match cities with their corresponding states
      const matchedCities = [];
      for (let i = 0; i < cityNames.length; i++) {
        const cityName = cityNames[i];
        const stateName = stateNames[i];
        const match = availableCities.find(
          (c) => c.city === cityName && c.state === stateName
        );
        if (match) {
          matchedCities.push(match);
        }
      }
      setSelectedCities(matchedCities);
    }

    // Check if we have all required parameters for auto-search
    if (zip && rent && bedrooms && backendReady) {
      setShouldAutoSearch(true);
    }
  }, [backendReady, availableCities]);

  // Separate effect to trigger search after state updates
  useEffect(() => {
    if (
      shouldAutoSearch &&
      userZip &&
      userRent &&
      userBedrooms &&
      backendReady
    ) {
      // Reset the flag immediately to prevent multiple triggers
      setShouldAutoSearch(false);
      // Small delay to ensure all state updates are complete
      setTimeout(() => {
        handleSearch();
      }, 100);
    }
  }, [
    shouldAutoSearch,
    userZip,
    userRent,
    userBedrooms,
    selectedCities,
    backendReady,
    handleSearch,
  ]);

  // Check if backend is ready on mount
  useEffect(() => {
    const checkBackend = async () => {
      try {
        const response = await fetch(`${API_URL}/health`);
        const data = await response.json();
        if (data.data_loaded) {
          setBackendReady(true);
          setError("");
          // Fetch available cities
          fetchCities();
        } else {
          setError("Backend is running but data not loaded");
        }
      } catch (err) {
        setError(
          "Cannot connect to backend. Make sure server is running on port 8000."
        );
      }
    };
    checkBackend();
  }, []);

  const fetchCities = async () => {
    try {
      const response = await fetch(`${API_URL}/cities`);
      const data = await response.json();
      setAvailableCities(data);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleCitySelect = (city) => {
    if (
      !selectedCities.find(
        (c) => c.city === city.city && c.state === city.state
      )
    ) {
      setSelectedCities([...selectedCities, city]);
      // Track city selection
      trackCitySelection(city.city, city.state);
    }
    setCitySearch("");
    setShowCityDropdown(false);
  };

  const handleCityRemove = (cityToRemove) => {
    setSelectedCities(
      selectedCities.filter(
        (c) => !(c.city === cityToRemove.city && c.state === cityToRemove.state)
      )
    );
  };

  const filteredCities = availableCities
    .filter((city) =>
      `${city.city}, ${city.state}`
        .toLowerCase()
        .includes(citySearch.toLowerCase())
    )
    .slice(0, 10);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Sharing functions
  const generateShareableLink = () => {
    const params = new URLSearchParams({
      zip: userZip,
      rent: userRent,
      bedrooms: userBedrooms,
    });
    if (selectedCities.length > 0) {
      params.append("cities", selectedCities.map((c) => c.city).join(","));
      params.append("states", selectedCities.map((c) => c.state).join(","));
    }
    return `${window.location.origin}?${params.toString()}`;
  };

  const copyShareableLink = async () => {
    const link = generateShareableLink();
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      trackShare("Copy Link");
    } catch (err) {
      console.error("Failed to copy link:", err);
      trackError("Share Error", "Failed to copy link");
    }
  };

  const handleImageLoad = (imageUrl) => {
    setLoadedImages((prev) => new Set(prev).add(imageUrl));
  };

  const shareToTwitter = (text) => {
    const url = generateShareableLink();
    const tweetText = encodeURIComponent(text);
    window.open(
      `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(
        url
      )}`,
      "_blank"
    );
    trackShare("Twitter");
  };

  const shareToLinkedIn = () => {
    const url = generateShareableLink();
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url
      )}`,
      "_blank"
    );
    trackShare("LinkedIn");
  };

  const shareToFacebook = () => {
    const url = generateShareableLink();
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank"
    );
    trackShare("Facebook");
  };

  const getMainCardShareText = () => {
    if (!results) return "";
    const comparison = results.difference > 0 ? "above" : "below";
    return `I'm paying $${results.user_rent.toLocaleString()} for a ${
      results.bedrooms_display
    } in ${results.city_state}, which is ${Math.abs(
      results.percent_difference
    )}% ${comparison} market average!`;
  };

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden flex flex-col">
        <div className="max-w-6xl mx-auto w-full flex-grow px-4 sm:px-6 py-6">
          {/* Header */}
          <div className="mb-8 sm:mb-12 text-center px-2">
            <div className="inline-block mb-4">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-transparent bg-clip-text">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-2 tracking-tight pb-2">
                  RentCheckr
                </h1>
              </div>
              <div className="h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 rounded-full"></div>
            </div>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed px-4">
              Discover how your rent compares to market averages and find where
              your budget goes further
            </p>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mt-4 px-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800">
                📊 Market Analysis
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-emerald-100 text-emerald-800">
                🏙️ City Comparisons
              </span>
            </div>
          </div>

          {/* Backend Status */}
          {!backendReady && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-6">
              ⚠️ Give us a moment to connect. Thank you for your patience!
              <code className="bg-yellow-100 px-2 py-1 rounded">
                python app.py
              </code>
            </div>
          )}

          {/* Input Section */}
          {backendReady && (
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Zip Code
                  </label>
                  <input
                    type="text"
                    value={userZip}
                    onChange={(e) => setUserZip(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="e.g., 94115"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Rent
                  </label>
                  <input
                    type="number"
                    value={userRent}
                    onChange={(e) => setUserRent(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="e.g., 2780"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Bedrooms
                  </label>
                  <select
                    value={userBedrooms}
                    onChange={(e) => setUserBedrooms(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                  >
                    <option value="0BR">Studio</option>
                    <option value="1BR">1 Bedroom</option>
                    <option value="2BR">2 Bedrooms</option>
                    <option value="3BR">3 Bedrooms</option>
                    <option value="4BR">4 Bedrooms</option>
                  </select>
                </div>
                <div className="flex items-end sm:col-span-2 lg:col-span-1">
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-2 px-6 rounded-lg transition flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : null}
                    {loading ? "Searching..." : "Analyze Rent"}
                  </button>
                </div>
              </div>

              {/* City Comparison Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Compare with other cities (optional)
                </label>
                <div className="relative">
                  <div className="w-full min-h-[42px] px-2 py-1 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500 bg-white flex flex-wrap gap-2 items-center">
                    {selectedCities.map((city, idx) => (
                      <div
                        key={idx}
                        className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm flex items-center gap-1"
                      >
                        <span>
                          {city.city}, {city.state}
                        </span>
                        <button
                          onClick={() => handleCityRemove(city)}
                          className="hover:text-green-900 font-bold text-lg leading-none"
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => {
                        setCitySearch(e.target.value);
                        setShowCityDropdown(true);
                        // Track city search queries
                        if (e.target.value.length >= 3) {
                          trackCitySearch(e.target.value);
                        }
                      }}
                      onFocus={() => setShowCityDropdown(true)}
                      placeholder={
                        selectedCities.length === 0
                          ? "Search for a city..."
                          : ""
                      }
                      className="flex-1 min-w-[120px] px-2 py-1 outline-none"
                    />
                  </div>
                  {showCityDropdown &&
                    citySearch &&
                    filteredCities.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredCities.map((city, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleCitySelect(city)}
                            className="px-4 py-2 hover:bg-green-50 cursor-pointer"
                          >
                            {city.city}, {city.state}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>

              {/* Data Source Info Box */}
              <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-green-600 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-blue-900 mb-1">
                      About This Data
                    </h3>
                    <p className="text-xs text-blue-800 leading-relaxed">
                      Data from the U.S. Census Bureau's{" "}
                      <a
                        href="https://data.census.gov/table?q=B25031:+Median+Gross+Rent+by+Bedrooms&y=2024"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-blue-900 font-medium"
                      >
                        2024 American Community Survey
                      </a>{" "}
                      (5-Year Estimates, Table B25031). Figures represent the
                      median monthly cost (rent + utilities) for all households
                      in this ZIP code, based on survey data collected from
                      2020-2024. <strong>Note:</strong> This data may not
                      reflect current market conditions or price changes from
                      2025, as it represents historical averages and may vary
                      from current 'live' real estate listings.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mt-6">
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Results Section */}
          {results && (
            <div className="space-y-8">
              {/* Share Section */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Share2 className="w-5 h-5" />
                      Share Your Rent Comparison
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => shareToTwitter(getMainCardShareText())}
                        className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        Twitter
                      </button>
                      <button
                        onClick={shareToLinkedIn}
                        className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        LinkedIn
                      </button>
                      <button
                        onClick={shareToFacebook}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                        Facebook
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={copyShareableLink}
                    className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition flex items-center gap-2 border border-gray-300 w-full sm:w-auto"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-600 font-semibold">
                          Copied!
                        </span>
                      </>
                    ) : (
                      <>
                        <LinkIcon className="w-5 h-5" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* City Image and Market Comparison */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {results.city_image && (
                  <div className="relative h-64 overflow-hidden bg-gray-200">
                    {!loadedImages.has(results.city_image) && (
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 animate-pulse" />
                    )}
                    <img
                      src={results.city_image}
                      alt={results.city_state}
                      className={`w-full h-full object-cover transition-opacity duration-300 ${
                        loadedImages.has(results.city_image)
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                      loading="lazy"
                      onLoad={() => handleImageLoad(results.city_image)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h2 className="text-3xl font-bold">
                        {results.city_state}
                      </h2>
                      <p className="text-gray-200">ZIP {results.zip}</p>
                    </div>
                  </div>
                )}

                <div className="p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
                    Your Market Analysis
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-lg border border-green-200">
                      <p className="text-gray-600 text-sm font-semibold mb-2">
                        Your Rent
                      </p>
                      <p className="text-4xl font-bold text-green-600">
                        ${results.user_rent.toLocaleString()}
                      </p>
                      <p className="text-gray-600 text-sm mt-2">
                        {results.bedrooms_display}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-lg border border-gray-200">
                      <p className="text-gray-600 text-sm font-semibold mb-2">
                        Market Average
                      </p>
                      <p className="text-4xl font-bold text-gray-700">
                        ${results.average_price.toLocaleString()}
                      </p>
                      <p className="text-gray-600 text-sm mt-2">
                        {results.bedrooms_display}
                      </p>
                    </div>

                    <div
                      className={`bg-gradient-to-br p-6 rounded-lg border-2 ${
                        results.difference > 0
                          ? "from-red-50 to-red-100 border-red-300"
                          : "from-green-50 to-green-100 border-green-300"
                      }`}
                    >
                      <p className="text-gray-600 text-sm font-semibold mb-2">
                        DIFFERENCE
                      </p>
                      <p
                        className={`text-4xl font-bold ${
                          results.difference > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {results.difference > 0 ? "+" : "-"}$
                        {Math.abs(results.difference).toLocaleString()}
                      </p>
                      <p
                        className={`text-sm mt-2 ${
                          results.difference > 0
                            ? "text-red-600"
                            : "text-green-600"
                        }`}
                      >
                        {results.difference > 0 ? "+" : ""}
                        {results.percent_difference}% from average
                      </p>
                    </div>
                  </div>

                  {/* Cities Not Found Warning */}
                  {results.cities_not_found &&
                    results.cities_not_found.length > 0 && (
                      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-yellow-600 mt-0.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                              {results.cities_not_found.length === 1
                                ? "City Not Available"
                                : "Cities Not Available"}
                            </h3>
                            <p className="text-xs text-yellow-800 leading-relaxed">
                              No rental data available for:{" "}
                              <strong>
                                {results.cities_not_found.join(", ")}
                              </strong>
                              . This may be because Census data is not available
                              for this location, or the city may have limited
                              survey coverage.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* City Comparisons */}
              {results.city_comparisons &&
                results.city_comparisons.length > 0 && (
                  <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                      Your Rent in Selected Cities
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 mb-6">
                      See how your ${results.user_rent.toLocaleString()}{" "}
                      compares in these cities
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {results.city_comparisons.map((comp, idx) => (
                        <div
                          key={idx}
                          className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-green-300"
                        >
                          {/* City Image */}
                          {comp.city_image && (
                            <div className="relative h-40 overflow-hidden bg-gray-200">
                              {!loadedImages.has(comp.city_image) && (
                                <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 animate-pulse" />
                              )}
                              <img
                                src={comp.city_image}
                                alt={comp.city_state}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${
                                  loadedImages.has(comp.city_image)
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                                loading="lazy"
                                onLoad={() => handleImageLoad(comp.city_image)}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                              <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                <p className="font-bold text-lg">
                                  {comp.city_state}
                                </p>
                                <p className="text-sm text-gray-200">
                                  ZIP {comp.zip}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Card Content */}
                          <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50">
                            {!comp.city_image && (
                              <>
                                <p className="text-gray-600 text-sm font-semibold mb-1">
                                  {comp.city_state}
                                </p>
                                <p className="text-gray-500 text-xs mb-3">
                                  ZIP {comp.zip}
                                </p>
                              </>
                            )}

                            {/* What you could afford */}
                            {comp.affordable_bedrooms && (
                              <div className="mb-4 bg-white rounded-lg p-4 border-2 border-green-200">
                                <p className="text-xs text-gray-600 font-semibold mb-2">
                                  With Your $
                                  {results.user_rent.toLocaleString()} You Could
                                  Get
                                </p>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-2xl font-bold text-green-600">
                                      {comp.affordable_bedrooms_display}
                                    </p>
                                    <p className="text-xs font-bold text-gray-500 mt-1">
                                      for $
                                      {comp.affordable_price?.toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Market comparison */}
                            <div className="bg-white rounded p-3 border border-green-200">
                              <p className="text-gray-600 text-xs font-semibold mb-1">
                                {results.bedrooms_display} Market Average
                              </p>
                              <p className="text-2xl font-bold text-gray-700">
                                ${comp.price.toLocaleString()}
                              </p>
                              <p
                                className={`text-sm mt-1 font-semibold ${
                                  comp.difference > 0
                                    ? "text-red-600"
                                    : "text-green-600"
                                }`}
                              >
                                {comp.difference > 0 ? "+" : ""}
                                {comp.percent_difference}% from your rent
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Alternative Markets */}
              {results.alternatives && results.alternatives.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                    Where Your ${results.user_rent.toLocaleString()} Goes
                    Further
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-6">
                    Top desirable cities where you could afford at this budget
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {results.alternatives.map((alt, idx) => (
                      <div
                        key={idx}
                        className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-green-300"
                      >
                        {/* City Image */}
                        {alt.city_image && (
                          <div className="relative h-40 overflow-hidden bg-gray-200">
                            {!loadedImages.has(alt.city_image) && (
                              <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 animate-pulse" />
                            )}
                            <img
                              src={alt.city_image}
                              alt={alt.city_state}
                              className={`w-full h-full object-cover transition-opacity duration-300 ${
                                loadedImages.has(alt.city_image)
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                              loading="lazy"
                              onLoad={() => handleImageLoad(alt.city_image)}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                              <p className="font-bold text-lg">
                                {alt.city_state}
                              </p>
                              <p className="text-sm text-gray-200">
                                ZIP {alt.zip}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Card Content */}
                        <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50">
                          {!alt.city_image && (
                            <>
                              <p className="text-gray-600 text-sm font-semibold mb-1">
                                {alt.city_state}
                              </p>
                              <p className="text-gray-500 text-xs mb-3">
                                ZIP {alt.zip}
                              </p>
                            </>
                          )}

                          <div className="mb-4">
                            <p className="text-xs text-gray-600 font-semibold mb-2">
                              Best Value In Your Budget
                            </p>
                            <div className="flex items-center justify-center">
                              <div className="text-center">
                                <p className="text-3xl font-bold text-green-600">
                                  {alt.bedrooms_display}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  apartment
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white rounded p-3 border border-green-200">
                            <p className="text-gray-600 text-xs font-semibold mb-1">
                              Budget
                            </p>
                            <p className="text-2xl font-bold text-green-600">
                              ${alt.price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-8 sm:mt-16 pb-8 w-full">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="border-t border-gray-300 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="text-center md:text-left">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                    RentCheckr
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    Making rent comparisons simple and transparent
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href="mailto:amittallapragada@gmail.com"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Contact Us
                  </a>
                </div>
              </div>
              <div className="mt-6 text-center text-xs sm:text-sm text-gray-500">
                <p>
                  © {new Date().getFullYear()} RentCheckr. Data provided by U.S.
                  Census Bureau.
                </p>
                <p className="mt-2">
                  Follow me{" "}
                  <a
                    href="https://x.com/penguinsawce"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 font-medium hover:underline"
                  >
                    @penguinsawce
                  </a>{" "}
                  for updates on my projects like this
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
  );
}
